import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data.user);
          setToken(storedToken);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, senha) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, senha });
      const { token: newToken, user: userData } = response.data;
      if (!newToken) {
        return { success: false, error: 'Servidor não retornou token — tente novamente' };
      }
      try {
        localStorage.setItem('token', newToken);
      } catch (storageErr) {
        // iOS Safari em modo privado bloqueia localStorage
        console.warn('localStorage bloqueado:', storageErr);
      }
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      // Mensagem detalhada
      let msg = 'Erro ao fazer login';
      if (error.response) {
        const d = error.response.data?.detail;
        if (typeof d === 'string') msg = d;
        else if (error.response.status === 401) msg = 'E-mail ou senha incorretos';
        else if (error.response.status === 429) msg = 'Muitas tentativas — aguarde 1 minuto';
        else if (error.response.status >= 500) msg = 'Servidor instável — tente em 30s';
      } else if (error.request) {
        msg = 'Sem conexão com o servidor. Verifique sua internet.';
      }
      return { success: false, error: msg };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, data);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, message: response.data.message, token: newToken };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Erro ao registrar';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Erros de validação do Pydantic (FastAPI) → transforma em mensagem legível
        const labels = {
          email: 'E-mail', senha: 'Senha', nome_completo: 'Nome completo', cpf: 'CPF', rg: 'RG',
          endereco: 'Endereço', telefone: 'Telefone', como_conheceu: 'Como conheceu',
          tamanho_camiseta: 'Tamanho da camiseta', tipo_camiseta: 'Tipo da camiseta',
          chave_pix: 'Chave PIX', divisao: 'Divisão', aceite_regulamento: 'Aceite do regulamento',
          modelo_moto: 'Modelo da moto', placa_moto: 'Placa da moto',
        };
        const campos = detail
          .filter(e => e?.loc?.length)
          .map(e => labels[e.loc[e.loc.length - 1]] || e.loc[e.loc.length - 1])
          .filter(Boolean);
        if (campos.length) msg = `Preencha os campos: ${campos.join(', ')}`;
      }
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return null;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};